import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { useAction } from "../../../components/ui";
import { date, time } from "../../../lib/format";
import { talentName } from "../../../data/queries";
import {
  endSupportGrant, handover, inviteMember, loadTeam, resendInvitation, revokeInvitation, saveAssignments, saveMember,
  type InvitationRow, type MemberRow, type TeamData,
} from "./data";
import {
  CHEF_POWERS, RIGHTS, RIGHTS_PRESETS, RIGHT_HINTS, assignmentDiff, auditText, invitationState, needsAmounts, normalizeRights, presenceLabel, presetOf, rightsPatch, usage,
  validateInvite, type RightsState,
} from "./model";
import { NurAdmin, CheckField, Confirm, FormErrors, SelectField, SettingsFrame, TextField } from "./parts";
import { ZweiFaktorPflicht } from "./ZweiFaktorPflicht";
import { NurLesenFeld } from "../../../app/nurLesen";
import { GrenzeWachsen, TarifHinweis, useModul } from "../../../components/Tarif";
import { platzGrenzeText, tarifName, testFreiText } from "../../../model/tarif";
import { t, ta } from "../../../lib/sprache";

/**
 * Team & Rechte (11_app_agentur/einstellungen-team.html): Rechte-Matrix je Manager, Creator-Zuweisungen, Einladungen mit Name
 * und Rechten, Namen und Funktionen im Team, dazu Creator-Aktivität (AD-211), Support-Zugänge und das Protokoll der Team-Änderungen.
 * 0099 (Tarifstaffel): Ohne Modul „Team-Rechte und Sichtbereiche“ ist nur das Einschränken gesperrt (ein Recht oder „alle Creator“
 * von an auf aus, neue Zuweisung); erweitern und Zuweisungen entfernen geht immer, neue Mitglieder bekommen volle Rechte.
 */
export function Team() {
  const ws = useStaffWorkspace();
  if (!ws.isChef) return <NurAdmin />;
  return <TeamSeite orgId={ws.org.org_id} orgName={ws.org.name} timeZone={ws.org.timezone} selfId={ws.membership.id} />;
}

function TeamSeite({ orgId, orgName, timeZone, selfId }: { orgId: string; orgName: string; timeZone: string; selfId: string }) {
  const q = useQuery({ queryKey: ["memberships", orgId, "team"], queryFn: () => loadTeam(api, orgId) });
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const d = q.data;
  const version = [...d.members.map((m) => m.updated_at), ...d.assignments.map((a) => a.membership_id + a.talent_id)].join();
  const chefs = d.members.filter((m) => m.active && m.role === "chef_manager");
  return (
    <SettingsFrame section="team" orgId={orgId} eyebrow="" title={t("Mitglieder und Rechte", "Members and permissions")}
      text={t("Sichtbereich, Finanzrechte, Beträge und das Gegenzeichnen von Verträgen pro Manager einzeln festlegen.", "Set the visible scope, financial permissions, amounts and contract countersigning individually per manager.")}
      actions={<Link className="btn" to="/agentur/profil">{t("Mein Profil", "My profile")}</Link>}
      notice={<div className="notice">{t("Admin: ", "Admins: ")}{chefs.map((c) => `${c.display_name ?? c.email ?? t("Ohne Namen", "Without name")}${c.is_owner ? t(" (Inhaber)", " (owner)") : ""}${c.via_group ? t(" (über den Verbund)", " (via the group)") : ""}`).join(", ")} {t("· Vollzugriff innerhalb von ", "· full access within ")}{orgName}.</div>}>
      {/* Nur-Lesen-Phase (RECHTE-R3-3): Rechte, Namen, Einladen, Übergabe und die Faktor-Pflicht gesperrt; Support-Zugang beenden bleibt */}
      <NurLesenFeld>
        <RechteBereich key={version} orgId={orgId} data={d} selfId={selfId} />
        <NamenPanel key={`namen-${version}`} data={d} />
        <Uebergabe key={`uebergabe-${version}`} orgId={orgId} data={d} selfId={selfId} />
      </NurLesenFeld>
      <TalentAktivitaet data={d} timeZone={timeZone} />
      {/* IA-1: Karte „Sicherheit fürs Team“ (Anker #sicherheit): Vorgaben für alle; den eigenen zweiten Faktor richtet jede Person
          im Profilmenü unter Anmeldung & Sicherheit ein */}
      <div className="stack tk-team-sicherheit" id="sicherheit">
        <h2>{t("Sicherheit fürs Team", "Team security")}</h2>
        <NurLesenFeld hinweis={false}><ZweiFaktorPflicht orgId={orgId} /></NurLesenFeld>
        <SupportZugaenge data={d} timeZone={timeZone} />
      </div>
      <Protokoll data={d} timeZone={timeZone} />
    </SettingsFrame>
  );
}

const rightsOf = (m: MemberRow): RightsState => ({
  role: m.role, sees_all_talents: m.sees_all_talents, can_approve_invoices: m.can_approve_invoices, can_send_invoices: m.can_send_invoices,
  can_dun: m.can_dun, can_confirm_payments: m.can_confirm_payments, can_trigger_payouts: m.can_trigger_payouts, can_sign_contracts: m.can_sign_contracts,
  can_edit_templates: !!m.can_edit_templates, can_edit_contract_text: !!m.can_edit_contract_text,
  can_view_financials: !!m.can_view_financials, can_view_amounts: m.can_view_amounts !== false,
});
/** Vertragsrechte aus 0112 (Vorlagen bearbeiten, Vertragstext ändern) */
const VERTRAGSRECHTE = new Set<keyof RightsState>(["can_edit_templates", "can_edit_contract_text"]);
/** Kennzahlen, Rechnungs- und Zahlungsrechte brauchen „Beträge sehen“: ohne Beträge sind die Schalter aus und gesperrt. */
const rightDisabled = (r: RightsState, key: string) => r.role === "chef_manager" || (needsAmounts(key) && !r.can_view_amounts);

function RechteBereich({ orgId, data, selfId }: { orgId: string; data: TeamData; selfId: string }) {
  const { busy, error, run } = useAction();
  const managers = data.members.filter((m) => m.active && m.role === "manager");
  const otherChefs = data.members.filter((m) => m.active && m.role === "chef_manager" && m.id !== selfId);
  const inactive = data.members.filter((m) => !m.active);
  const [rights, setRights] = useState<Record<string, RightsState>>(() => Object.fromEntries(managers.map((m) => [m.id, rightsOf(m)])));
  const initialAssign = (id: string) => data.assignments.filter((a) => a.membership_id === id).map((a) => a.talent_id);
  const [assign, setAssign] = useState<Record<string, string[]>>(() => Object.fromEntries(managers.map((m) => [m.id, initialAssign(m.id)])));
  const [selected, setSelected] = useState(managers.find((m) => !m.sees_all_talents)?.id ?? managers[0]?.id ?? "");
  const [endAccess, setEndAccess] = useState<MemberRow | null>(null);
  const [promote, setPromote] = useState<string[] | null>(null);
  const [demote, setDemote] = useState<MemberRow | null>(null);
  const talents = data.talents.filter((t) => t.status !== "archived");
  const name = (m: MemberRow) => m.display_name ?? m.email ?? t("Ohne Namen", "Without name");
  const selfOwner = !!data.members.find((m) => m.id === selfId)?.is_owner;
  // Tarifstaffel: Einschränken gesperrt, Erweitern frei (die Datenbank prüft dasselbe je Feld)
  const rightsLocked = useModul("module_rights").gesperrt;
  // Die Vertragsrechte (0112) gehören nicht zur Tarifstaffel: Entziehen geht immer (die Datenbank sperrt dort nur den Sichtbereich, 0105)
  const restrictLocked = (m: MemberRow, key: keyof RightsState) => rightsLocked && !VERTRAGSRECHTE.has(key) && rightsOf(m)[key] === true;

  const changes = managers.map((m) => ({ m, patch: rightsPatch(rightsOf(m), rights[m.id]!), diff: assignmentDiff(initialAssign(m.id), assign[m.id] ?? []) }))
    .filter((c) => c.patch || c.diff.add.length || c.diff.remove.length);

  /** Beförderung zum Admin nur mit Bestätigung (Befund 20) */
  function askSave() {
    const promoted = changes.filter((c) => c.patch?.role === "chef_manager").map((c) => name(c.m));
    if (promoted.length) setPromote(promoted);
    else save();
  }
  function save() {
    setPromote(null);
    void run("rechte", async () => {
      for (const c of changes) {
        if (c.patch) await saveMember(api, c.m.id, c.patch);
        if (c.diff.add.length || c.diff.remove.length) await saveAssignments(api, orgId, c.m.id, c.diff);
      }
    }, changes.length === 1 ? t("Rechte gespeichert. Das Protokoll hält die Änderung fest.", "Permissions saved. The log records the change.") : t("Rechte gespeichert. Das Protokoll hält die Änderungen fest.", "Permissions saved. The log records the changes."));
  }
  const setRight = (id: string, patch: Partial<RightsState>) => setRights((r) => ({ ...r, [id]: normalizeRights({ ...r[id]!, ...patch }) }));
  const sel = managers.find((m) => m.id === selected);

  return (
    <>
      {/* Je Manager eine Karte mit allen Rechten, auf jeder Breite (SICHT-AGENTUR-REST-23): Die Tabelle mit elf Spalten
          verdeckte schon bei 1440 px die Finanz- und Auszahlungsrechte ohne Hinweis. */}
      {rightsLocked && (
        <>
          <TarifHinweis modul="module_rights" id="rechte-tarif" was={t("Rechte einschränken, Sichtbereich und Zuweisungen", "Restricting permissions, visibility and assignments")} />
          <p className="muted">{t("In deinem Tarif haben alle Mitglieder volle Rechte und sehen alle Creator. Rechte erweitern und bestehende Einschränkungen aufheben geht immer.", "In your plan all members have full permissions and see all creators. Extending permissions and lifting existing restrictions always works.")}</p>
        </>
      )}
      {managers.length === 0 && <p>{t("Noch keine Manager im Team. Lade unten jemanden ein.", "No managers in the team yet. Invite someone below.")}</p>}
      <div className="tk-rechte-karten">
        {managers.map((m) => {
          const r = rights[m.id]!;
          const promoted = r.role === "chef_manager";
          return (
            <section key={m.id} className="panel stack" aria-label={t(`Rechte ${name(m)}`, `Permissions ${name(m)}`)}>
              <strong>{name(m)}</strong>{m.job_title && <small>{m.job_title}</small>}
              <label>
                {t("Rolle", "Role")}<select value={r.role} onChange={(e) => setRight(m.id, { role: e.target.value as RightsState["role"] })}>
                  <option value="manager">Manager</option>
                  <option value="chef_manager">{t("Admin", "Admin")}</option>
                </select>
              </label>
              <label>
                {t("Creator sehen", "See creators")}<select value={r.sees_all_talents ? "alle" : "zugewiesen"} disabled={promoted || restrictLocked(m, "sees_all_talents")}
                  aria-describedby={restrictLocked(m, "sees_all_talents") ? "rechte-tarif" : undefined} onChange={(e) => setRight(m.id, { sees_all_talents: e.target.value === "alle" })}>
                  <option value="zugewiesen">{t("Nur zugewiesene", "Assigned only")}</option>
                  <option value="alle">{t("Alle Creator", "All creators")}</option>
                </select>
              </label>
              {RIGHTS.map((x) => (
                <label key={x.key} className="row tk-check">
                  <input type="checkbox" checked={promoted || r[x.key]} disabled={rightDisabled(r, x.key) || restrictLocked(m, x.key)}
                    aria-describedby={restrictLocked(m, x.key) ? "rechte-tarif" : undefined} onChange={(e) => setRight(m.id, { [x.key]: e.target.checked } as Partial<RightsState>)} />
                  <span>{x.label}</span>
                </label>
              ))}
              <div className="row"><button type="button" className="danger" onClick={() => setEndAccess(m)}>{t("Zugang beenden", "End access")}</button></div>
            </section>
          );
        })}
      </div>
      {/* Speichern direkt unter den Rechten; mit ungespeicherten Änderungen bleibt die Leiste unten im Blick (SICHT-AGENTUR-REST-23) */}
      <FormErrors errors={{}} error={error} />
      <div className={`row tk-rechte-speichern${changes.length ? " tk-ungespeichert" : ""}`}>
        <button className="primary" type="button" disabled={!changes.length || !!busy} aria-busy={busy === "rechte"} onClick={askSave}>
          {busy === "rechte" ? t("Wird gespeichert …", "Saving …") : t("Rechte speichern", "Save permissions")}
        </button>
        {changes.length > 0 && <small>{changes.length === 1 ? t("1 Manager geändert", "1 manager changed") : t(`${changes.length} Manager geändert`, `${changes.length} managers changed`)} {t("· noch nicht gespeichert", "· not saved yet")}</small>}
      </div>
      <small>{t("„Kennzahlen und Agenturumsatz sehen“: ", "“View metrics and agency revenue”: ")}{RIGHT_HINTS.can_view_financials} {t("„Beträge sehen“: ", "“View amounts”: ")}{RIGHT_HINTS.can_view_amounts}</small>
      <small>{t("„Vorlagen bearbeiten“: ", "“Edit templates”: ")}{RIGHT_HINTS.can_edit_templates} {t("„Vertragstext ändern“: ", "“Change contract text”: ")}{RIGHT_HINTS.can_edit_contract_text}</small>

      <div className="grid tk-team-grid">
        <section className="panel stack" aria-labelledby="zuweisung-title">
          <h3 id="zuweisung-title">{t("Creator-Zuweisungen", "Creator assignments")}</h3>
          {managers.length === 0 ? <p>{t("Zuweisungen gibt es, sobald ein Manager im Team ist.", "Assignments are available as soon as there is a manager in the team.")}</p> : (
            <>
              <SelectField label="Manager" value={selected} onChange={setSelected} options={managers.map((m) => ({ key: m.id, label: name(m) }))} />
              {sel && (rights[sel.id]!.sees_all_talents || rights[sel.id]!.role === "chef_manager") && (
                <p>{name(sel)} {t("sieht alle Creator. Zuweisungen wirken erst mit „Nur zugewiesene“.", "sees all creators. Assignments only take effect with “Assigned only”.")}</p>
              )}
              {sel && rightsLocked && <small id="zuweisung-tarif">{t("Neue Zuweisungen schränken ein und sind in deinem Tarif gesperrt; bestehende lassen sich entfernen.", "New assignments restrict and are locked in your plan; existing ones can be removed.")}</small>}
              {sel && talents.map((tl) => (
                <label key={tl.id} className="row">
                  <input type="checkbox" checked={(assign[sel.id] ?? []).includes(tl.id)}
                    disabled={rightsLocked && !initialAssign(sel.id).includes(tl.id)} aria-describedby={rightsLocked ? "zuweisung-tarif" : undefined}
                    onChange={(e) => setAssign((a) => ({ ...a, [sel.id]: e.target.checked ? [...(a[sel.id] ?? []), tl.id] : (a[sel.id] ?? []).filter((x) => x !== tl.id) }))} />
                  {talentName(tl)}{tl.status === "invited" ? t(" (eingeladen)", " (invited)") : ""}
                </label>
              ))}
              {sel && talents.length === 0 && <p>{t("Noch keine Creator angelegt.", "No creators created yet.")}</p>}
            </>
          )}
        </section>
        <EinladenPanel orgId={orgId} data={data} />
      </div>

      <p>{t("Manager erhalten keine Einstellungen und kein Abo. Rechnungen prüfen bleibt Teil des Tagesgeschäfts; Freigabe ist ein getrenntes Recht.", "Managers get no settings and no subscription. Reviewing invoices remains part of daily work; approval is a separate permission.")}</p>

      {(otherChefs.length > 0 || inactive.length > 0) && (
        <section className="panel stack" aria-labelledby="weitere-title">
          <h3 id="weitere-title">{t("Weitere Admin und frühere Mitglieder", "Other admins and former members")}</h3>
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("Weitere Admin und frühere Teammitglieder", "Other admins and former team members")}</caption>
              <thead><tr><th scope="col">Name</th><th scope="col">{t("Rolle", "Role")}</th><th scope="col">Status</th><th scope="col">{t("Aktion", "Action")}</th></tr></thead>
              <tbody>
                {otherChefs.map((m) => (
                  <tr key={m.id}>
                    <td>{name(m)}</td>
                    <td>{t("Admin", "Admin")}{m.is_owner ? t(" · Inhaber", " · owner") : ""}{m.via_group ? t(" · über den Verbund", " · via the group") : ""}</td>
                    <td><span className="pill success"><span className="pill-zeichen">✓</span> {t("Aktiv", "Active")}</span></td>
                    <td>
                      {m.via_group
                        ? <small>{t("Gruppen-Admin. Rolle und Zugang legt der Betrieb im Verbund fest.", "Group admin. The operator sets role and access in the group.")}</small>
                        : m.is_owner && !selfOwner
                        ? <small>{t("Den Inhaber stuft nur die Person selbst herab (Übergabe).", "Only the owner can demote themselves (handover).")}</small>
                        : <button type="button" disabled={!!busy} onClick={() => setDemote(m)}>{t("Zum Manager machen", "Make manager")}</button>}
                    </td>
                  </tr>
                ))}
                {inactive.map((m) => (
                  <tr key={m.id} className="tk-muted-row">
                    <td>{name(m)}</td>
                    <td>{m.role === "chef_manager" ? t("Admin", "Admin") : "Manager"}{m.via_group ? t(" · über den Verbund", " · via the group") : ""}</td>
                    <td><span className="pill"><span className="pill-zeichen">○</span> {t("Kein Zugang", "No access")}</span></td>
                    <td>{m.via_group ? <small>{t("Kommt mit dem nächsten Abgleich des Verbunds zurück.", "Returns with the next group sync.")}</small> : <button type="button" disabled={!!busy} onClick={() => void run("aktiv", () => saveMember(api, m.id, { active: true }), t(`${name(m)} hat wieder Zugang.`, `${name(m)} has access again.`))}>{t("Wieder aktivieren", "Reactivate")}</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <Confirm open={!!promote} title={t("Zum Admin machen?", "Make admin?")} busy={busy === "rechte"} confirmLabel={t("Ja, Rechte speichern", "Yes, save permissions")}
        text={promote ? t(`${promote.join(" und ")} ${promote.length === 1 ? "wird" : "werden"} Admin. ${CHEF_POWERS()} Alle Admins bekommen eine Mitteilung.`, `${promote.join(" and ")} ${promote.length === 1 ? "becomes a" : "become"} admin${promote.length === 1 ? "" : "s"}. ${CHEF_POWERS()} All admins get a notification.`) : ""}
        onClose={() => setPromote(null)} onConfirm={save} />
      <Confirm open={!!demote} title={t("Zum Manager machen?", "Make manager?")} busy={busy === "rolle"} confirmLabel={t("Zum Manager machen", "Make manager")}
        text={demote ? t(`${name(demote)} verliert Einstellungen, Team und Abo und behält als Manager die bisherigen Creator-Rechte. Alle Admins und die Person selbst bekommen eine Mitteilung.`, `${name(demote)} loses settings, team and subscription and keeps the previous creator permissions as a manager. All admins and the person themselves get a notification.`) : ""}
        onClose={() => setDemote(null)}
        onConfirm={() => void run("rolle", () => saveMember(api, demote!.id, { role: "manager" }), t(`${demote ? name(demote) : ""} ist jetzt Manager.`, `${demote ? name(demote) : ""} is now a manager.`)).then(() => setDemote(null))} />
      <Confirm open={!!endAccess} title={t("Zugang beenden?", "End access?")} danger busy={busy === "ende"} confirmLabel={t("Zugang beenden", "End access")}
        text={endAccess ? t(`${name(endAccess)} verliert sofort den Zugang zu dieser Agentur. Daten und Protokoll bleiben erhalten; der Zugang lässt sich später wieder aktivieren.`, `${name(endAccess)} immediately loses access to this agency. Data and log are kept; access can be reactivated later.`) : ""}
        onClose={() => setEndAccess(null)}
        onConfirm={() => void run("ende", () => saveMember(api, endAccess!.id, { active: false }), t("Zugang beendet.", "Access ended.")).then(() => setEndAccess(null))} />
    </>
  );
}

type InviteRights = Omit<RightsState, "role">;
/** Standardrechte neuer Manager (Design, app.default_member_rights in 0042): nur zugewiesene Creator, keine Finanzaktionen. */
const defaultInviteRights = (): InviteRights => ({ sees_all_talents: false, ...Object.fromEntries(RIGHTS.map((r) => [r.key, r.key === "can_view_amounts"])) }) as InviteRights;

/**
 * Volle Rechte (Tarif ohne „Team-Rechte“, 0099: die Datenbank gibt neuen Mitgliedern ohnehin alle Rechte). Die Vertragsrechte
 * (0112) gehören nicht dazu: Sie bleiben aus, bis ein Admin sie ausdrücklich vergibt (Standard aus 0112).
 */
const fullInviteRights = (): InviteRights => ({ sees_all_talents: true, ...Object.fromEntries(RIGHTS.map((r) => [r.key, !VERTRAGSRECHTE.has(r.key)])) }) as InviteRights;

function EinladenPanel({ orgId, data }: { orgId: string; data: TeamData }) {
  const { busy, error, run } = useAction();
  const rightsLocked = useModul("module_rights").gesperrt;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"manager" | "chef_manager">("manager");
  const [rights, setRights] = useState<InviteRights>(defaultInviteRights);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const now = Date.now();
  const open = data.invitations.filter((i) => invitationState(i, now) === "offen" || invitationState(i, now) === "abgelaufen");
  // Plätze: aktive Mitglieder und noch gültige Einladungen (die Datenbank prüft dasselbe beim Einladen)
  const reserved = data.invitations.filter((i) => invitationState(i, now) === "offen").length;
  const seats = usage(data.seats.used + reserved, data.seats.max);
  // K-374: „Upgrade auf <nächster Tarif>“ öffnet direkt den Wechseldialog; im größten Tarif die Zusatzplätze unter Abo & Tarif

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const v = validateInvite(email, name);
    setFieldErrors(v.errors);
    if (!v.email || Object.keys(v.errors).length) return;
    void run("einladen", () => inviteMember(api, orgId, v.email!, role, { name: v.name, rights: rightsLocked ? fullInviteRights() : rights }),
      api.mode === "demo" ? t("Einladung erstellt. In der Demo wird keine Mail verschickt.", "Invitation created. In the demo no email is sent.") : t("Einladung erstellt. Die Mail mit dem Link geht an die Adresse.", "Invitation created. The email with the link goes to the address."))
      .then((r) => {
        if (!r) return;
        setEmail("");
        setName("");
        setRights(defaultInviteRights());
      });
  }
  return (
    <section className="panel stack" aria-labelledby="einladen-title">
      <h3 id="einladen-title">{t("Manager einladen", "Invite manager")}</h3>
      <form className="stack" onSubmit={submit} noValidate aria-busy={busy === "einladen"}>
        <TextField label={t("E-Mail", "Email")} type="email" value={email} onChange={setEmail} error={fieldErrors.email} required autoComplete="off" />
        <TextField label={t("Name im Team", "Name in the team")} value={name} onChange={setName} error={fieldErrors.name} autoComplete="off"
          hint={t("Erscheint in Team, Protokoll und Chat. Die Person kann ihn später im Profil ändern.", "Appears in team, log and chat. The person can change it later in their profile.")} />
        <SelectField label={t("Rolle", "Role")} value={role} onChange={(v) => setRole(v as typeof role)} options={[{ key: "manager", label: "Manager" }, { key: "chef_manager", label: t("Admin", "Admin") }]} />
        {role === "manager" && rightsLocked ? (
          <div className="stack">
            <p>{t("Neue Manager bekommen in deinem Tarif volle Rechte und sehen alle Creator.", "In your plan new managers get full permissions and see all creators.")}</p>
            <TarifHinweis modul="module_rights" was={t("Rechte je Manager festlegen", "Setting permissions per manager")} />
          </div>
        ) : role === "manager" ? (
          <fieldset className="stack">
            <legend>{t("Rechte ab der Annahme", "Permissions from acceptance")}</legend>
            <SelectField label={t("Vorlage", "Template")} value={presetOf(rights) ?? ""} placeholder={t("Eigene Auswahl", "Custom selection")}
              onChange={(v) => { const p = RIGHTS_PRESETS.find((x) => x.key === v); if (p) setRights({ ...p.rights }); }}
              options={RIGHTS_PRESETS.map((p) => ({ key: p.key, label: p.label }))}
              hint={RIGHTS_PRESETS.find((p) => p.key === presetOf(rights))?.text ?? t("Häkchen einzeln setzen oder eine Vorlage wählen.", "Tick individually or choose a template.")} />
            <CheckField label={t("Alle Creator sehen (sonst nur zugewiesene)", "See all creators (otherwise only assigned ones)")} checked={rights.sees_all_talents} onChange={(v) => setRights((r) => ({ ...r, sees_all_talents: v }))} />
            {RIGHTS.map((x) => (
              <CheckField key={x.key} label={x.label} checked={rights[x.key]} disabled={needsAmounts(x.key) && !rights.can_view_amounts}
                onChange={(v) => setRights((r) => normalizeRights({ ...r, [x.key]: v }))} />
            ))}
            <small>{t("Neue Manager starten ohne Finanzrechte und sehen nur die Creator, die du ihnen zuweist. Nach der Annahme änderst du die Rechte in der Tabelle oben.", "New managers start without financial permissions and only see the creators you assign to them. After acceptance you change the permissions in the table above.")}</small>
          </fieldset>
        ) : (
          <p>{t("Admins haben alle Rechte, auch Einstellungen, Team und Abo.", "Admins have all permissions, including settings, team and subscription.")}</p>
        )}
        <p>
          {t("Team-Plätze: ", "Team seats: ")}{seats.text}{reserved ? t(` (davon ${reserved === 1 ? t("1 offene Einladung", "1 open invitation") : t(`${reserved} offene Einladungen`, `${reserved} open invitations`)})`, ` (of which ${reserved === 1 ? t("1 offene Einladung", "1 open invitation") : t(`${reserved} offene Einladungen`, `${reserved} open invitations`)})`) : ""}
          {/* K-377: im Test gelten die Grenzen des vollen Umfangs; einmal der Satz, was danach gilt */}
          {data.test && <> · {testFreiText(data.test.bis, tarifName(data.test.planKey), platzGrenzeText(data.test.planSeats))}.</>}
          {/* K-490: beide Wege mit Preis (weiterer Platz oder größerer Tarif) */}
          {seats.full && <> {t("· alle belegt oder reserviert. Ziehe eine Einladung zurück oder beende einen Zugang.", "· all used or reserved. Withdraw an invitation or end an access.")}<GrenzeWachsen art="seats" /></>}
        </p>
        <FormErrors errors={{}} error={busy ? null : error} />
        <button className="" type="submit" disabled={!!busy || seats.full}>{busy === "einladen" ? t("Wird erstellt …", "Creating …") : t("Einladung senden", "Send invitation")}</button>
      </form>
      {open.length > 0 && <Einladungen invitations={open} />}
    </section>
  );
}

function Einladungen({ invitations }: { invitations: InvitationRow[] }) {
  const { busy, run } = useAction();
  const now = Date.now();
  return (
    <div className="tablewrap">
      <table>
        <caption className="sr-only">{t("Offene Einladungen ins Team", "Open team invitations")}</caption>
        <thead><tr><th scope="col">{t("E-Mail", "Email")}</th><th scope="col">{t("Rolle", "Role")}</th><th scope="col">Status</th><th scope="col">{t("Aktion", "Action")}</th></tr></thead>
        <tbody>
          {invitations.map((i) => {
            const st = invitationState(i, now);
            return (
              <tr key={i.id}>
                <td>{i.email}<small>{t("gültig bis ", "valid until ")}{date(i.expires_at)}</small></td>
                <td>{i.role === "chef_manager" ? t("Admin", "Admin") : "Manager"}</td>
                <td>{st === "abgelaufen" ? <span className="pill error"><span className="pill-zeichen">!</span> {t("Abgelaufen", "Expired")}</span> : <span className="pill warning"><span className="pill-zeichen">◷</span> {t("Offen", "Open")}</span>}</td>
                <td>
                  <div className="row">
                    <button type="button" disabled={!!busy} onClick={() => void run(`neu-${i.id}`, () => resendInvitation(api, i.id), t("Einladung erneut gesendet; der alte Link gilt nicht mehr.", "Invitation resent; the old link no longer works."))}>{t("Erneut senden", "Send again")}</button>
                    <button type="button" className="danger" disabled={!!busy} onClick={() => void run(`weg-${i.id}`, () => revokeInvitation(api, i.id), t("Einladung zurückgezogen.", "Invitation withdrawn."))}>{t("Zurückziehen", "Withdraw")}</button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function NamenPanel({ data }: { data: TeamData }) {
  const { busy, error, run } = useAction();
  const active = data.members.filter((m) => m.active);
  const [draft, setDraft] = useState<Record<string, { display_name: string; job_title: string }>>(() =>
    Object.fromEntries(active.map((m) => [m.id, { display_name: m.display_name ?? "", job_title: m.job_title ?? "" }])));
  const changed = active.filter((m) => draft[m.id]!.display_name.trim() !== (m.display_name ?? "") || draft[m.id]!.job_title.trim() !== (m.job_title ?? ""));
  const invalid = changed.filter((m) => draft[m.id]!.display_name.trim().length < 2);
  function save() {
    void run("namen", async () => {
      for (const m of changed) await saveMember(api, m.id, { display_name: draft[m.id]!.display_name.trim(), job_title: draft[m.id]!.job_title.trim() || null });
    }, changed.length === 1 ? t("Name gespeichert.", "Name saved.") : t("Namen gespeichert.", "Names saved."));
  }
  return (
    <section className="panel stack" aria-labelledby="namen-title">
      <h3 id="namen-title">{t("Namen und Funktionen", "Names and job titles")}</h3>
      <p>{t("So erscheint das Team in Kooperationen, im Chat mit Creatorn und im Protokoll. Jede Person kann ihren Namen auch selbst im Profil ändern.", "This is how the team appears in deals, in chats with creators and in the log. Each person can also change their name themselves in their profile.")}</p>
      <div className="tablewrap tk-cardtable">
        <table>
          <caption className="sr-only">{t("Namen und Funktionen im Team", "Names and job titles in the team")}</caption>
          <thead><tr><th scope="col">{t("Anzeigename", "Display name")}</th><th scope="col">{t("Funktion", "Job title")}</th><th scope="col">{t("Rolle", "Role")}</th></tr></thead>
          <tbody>
            {active.map((m) => (
              <tr key={m.id}>
                <td data-label={t("Anzeigename", "Display name")}><input className="tk-table-input" aria-label={t("Anzeigename", "Display name")} value={draft[m.id]!.display_name} placeholder={m.email ?? t("Ohne Namen", "Without name")}
                  onChange={(e) => setDraft((d) => ({ ...d, [m.id]: { ...d[m.id]!, display_name: e.target.value } }))} /></td>
                <td data-label={t("Funktion", "Job title")}><input className="tk-table-input" aria-label={t("Funktion", "Job title")} value={draft[m.id]!.job_title} placeholder={t("z. B. Talent-Management", "e.g. talent management")}
                  onChange={(e) => setDraft((d) => ({ ...d, [m.id]: { ...d[m.id]!, job_title: e.target.value } }))} /></td>
                <td data-label={t("Rolle", "Role")}>{m.role === "chef_manager" ? t("Admin", "Admin") : "Manager"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {invalid.length > 0 && <small className="tk-error" role="alert">{t("Ein Anzeigename braucht mindestens zwei Zeichen.", "A display name needs at least two characters.")}</small>}
      <FormErrors errors={{}} error={error} />
      <div className="row">
        <button type="button" disabled={!changed.length || invalid.length > 0 || !!busy} aria-busy={busy === "namen"} onClick={save}>
          {busy === "namen" ? t("Wird gespeichert …", "Saving …") : t("Namen speichern", "Save names")}
        </button>
      </div>
    </section>
  );
}

/**
 * Übergabe der Agentur (Befund 20, Praxisfall „Chef verlässt die Firma“): neue Person wird Admin (und Inhaber),
 * Vertretung in Firma und Antwortadresse folgen, die übergebende Person bleibt, wird Manager oder beendet ihren Zugang.
 */
function Uebergabe({ orgId, data, selfId }: { orgId: string; data: TeamData; selfId: string }) {
  const { busy, error, run } = useAction();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = data.members.find((m) => m.id === selfId);
  const others = data.members.filter((m) => m.active && m.id !== selfId);
  const [to, setTo] = useState(others[0]?.id ?? "");
  const target = others.find((m) => m.id === to);
  const [rep, setRep] = useState(target?.display_name ?? "");
  const [pos, setPos] = useState("Geschäftsführung");
  const [reply, setReply] = useState("");
  const [self, setSelf] = useState<"keep" | "manager" | "leave">("manager");
  const [confirm, setConfirm] = useState(false);
  const owner = !!me?.is_owner;
  if (!others.length) {
    return (
      <section className="panel stack" aria-labelledby="uebergabe-title">
        <h3 id="uebergabe-title">{t("Agentur übergeben", "Hand over agency")}</h3>
        <p>{t("Lade zuerst die Person ein, die die Agentur übernehmen soll. Danach übergibst du hier Rolle, Vertretung und Antwortadresse in einem Schritt.", "First invite the person who is to take over the agency. Then you hand over role, representation and reply address here in one step.")}</p>
      </section>
    );
  }
  const name = target?.display_name ?? t("die gewählte Person", "the selected person");
  return (
    <section className="panel stack" aria-labelledby="uebergabe-title">
      <h3 id="uebergabe-title">{t("Agentur übergeben", "Hand over agency")}</h3>
      <p>{t("Für den Wechsel der Leitung: die neue Person wird Admin", "For a change of management: the new person becomes admin")}{owner ? t(" und Inhaber", " and owner") : ""}{t(", „Vertreten durch“ in Firma & Recht und die Antwortadresse der Mails folgen auf Wunsch gleich mit.", "; “Represented by” in Company & legal and the reply address of emails follow at the same time if desired.")}</p>
      <div className="grid">
        <label>
          {t("Übergeben an", "Hand over to")}<select value={to} onChange={(e) => { setTo(e.target.value); setRep(others.find((m) => m.id === e.target.value)?.display_name ?? ""); }}>
            {others.map((m) => <option key={m.id} value={m.id}>{m.display_name ?? m.email ?? t("Ohne Namen", "Without name")} · {m.role === "chef_manager" ? t("Admin", "Admin") : "Manager"}</option>)}
          </select>
        </label>
        <label>
          {t("Deine Rolle danach", "Your role afterwards")}<select value={self} onChange={(e) => setSelf(e.target.value as typeof self)}>
            <option value="keep">{t("Ich bleibe Admin", "I remain admin")}</option>
            <option value="manager">{t("Ich werde Manager", "I become a manager")}</option>
            <option value="leave">{t("Ich beende meinen Zugang", "I end my access")}</option>
          </select>
        </label>
      </div>
      <div className="grid">
        <TextField label={t("Vertreten durch (Firma & Recht)", "Represented by (Company & legal)")} value={rep} onChange={setRep} hint={t("Leer lassen: bleibt wie bisher.", "Leave empty: stays as before.")} />
        <TextField label={t("Funktion", "Job title")} value={pos} onChange={setPos} />
      </div>
      <TextField label={t("Neue Antwortadresse der Mails", "New reply address for emails")} type="email" value={reply} onChange={setReply} hint={t("Leer lassen: bleibt wie bisher.", "Leave empty: stays as before.")} autoComplete="off" />
      {error && !confirm && <p className="tk-error" role="alert">{error}</p>}
      <div className="row"><button type="button" onClick={() => setConfirm(true)} disabled={!to || !!busy}>{t("Übergabe vorbereiten", "Prepare handover")}</button></div>
      <Confirm open={confirm} title={t("Agentur übergeben?", "Hand over agency?")} busy={busy === "uebergabe"} confirmLabel={t("Jetzt übergeben", "Hand over now")} onClose={() => setConfirm(false)}
        text={t(`${name} wird Admin${owner ? t(" und Inhaber", " and owner") : ""}. ${CHEF_POWERS()}${rep.trim() ? t(` „Vertreten durch“ wird ${rep.trim()}.`, ` “Represented by” becomes ${rep.trim()}.`) : ""}${reply.trim() ? t(` Antworten auf Mails gehen an ${reply.trim()}.`, ` Replies to emails go to ${reply.trim()}.`) : ""} ${self === "keep" ? t("Du bleibst Admin.", "You remain admin.") : self === "manager" ? t("Du wirst Manager.", "You become a manager.") : t("Dein Zugang endet sofort.", "Your access ends immediately.")} Alle Admins bekommen eine Mitteilung.`, `${name} becomes admin${owner ? t(" und Inhaber", " and owner") : ""}. ${CHEF_POWERS()}${rep.trim() ? t(` „Vertreten durch“ wird ${rep.trim()}.`, ` “Represented by” becomes ${rep.trim()}.`) : ""}${reply.trim() ? t(` Antworten auf Mails gehen an ${reply.trim()}.`, ` Replies to emails go to ${reply.trim()}.`) : ""} ${self === "keep" ? t("Du bleibst Admin.", "You remain admin.") : self === "manager" ? t("Du wirst Manager.", "You become a manager.") : t("Dein Zugang endet sofort.", "Your access ends immediately.")} All admins get a notification.`)}
        onConfirm={() => void run("uebergabe", async () => {
          await handover(api, orgId, to, { self, transfer_owner: owner, representative_name: rep.trim() || null, representative_position: rep.trim() ? pos.trim() || null : null, reply_to_email: reply.trim() || null });
          await qc.invalidateQueries({ queryKey: ["account"] });
          return true;
        }, t("Agentur übergeben. Das Protokoll hält die Änderung fest.", "Agency handed over. The log records the change.")).then((ok) => {
          setConfirm(false);
          if (ok && self !== "keep") navigate("/", { replace: true });
        })} />
    </section>
  );
}

function TalentAktivitaet({ data, timeZone }: { data: TeamData; timeZone: string }) {
  const now = Date.now();
  const byTalent = new Map(data.presence.map((p) => [p.talent_id, p]));
  const rows = data.talents.filter((t) => t.status !== "archived");
  return (
    <section className="panel stack" aria-labelledby="aktivitaet-title">
      <h3 id="aktivitaet-title">{t("Creator-Aktivität", "Creator activity")}</h3>
      <div className="tablewrap tk-cardtable">
        <table>
          <caption className="sr-only">{t("Wann Creator zuletzt im Portal waren", "When creators were last in the portal")}</caption>
          <thead><tr><th scope="col">Creator</th><th scope="col">{t("Zuletzt gesehen", "Last seen")}</th><th scope="col">{t("Letzte Anmeldung", "Last sign-in")}</th><th scope="col">{t("Letzte Ansicht", "Last view")}</th></tr></thead>
          <tbody>
            {rows.map((tl) => {
              const p = byTalent.get(tl.id);
              const online = p?.last_seen_at && now - Date.parse(p.last_seen_at) < 5 * 60000;
              return (
                <tr key={tl.id}>
                  <td data-label="Creator">{talentName(tl)}{tl.status === "invited" && <small>{t("eingeladen, noch kein Zugang", "invited, no access yet")}</small>}</td>
                  <td data-label={t("Zuletzt gesehen", "Last seen")}>{online ? <span className="pill success"><span className="pill-zeichen">✓</span> {t("Gerade online", "Online now")}</span> : presenceLabel(p?.last_seen_at, now, (iso) => date(iso, timeZone))}</td>
                  <td data-label={t("Letzte Anmeldung", "Last sign-in")}>{p?.last_login_at ? `${date(p.last_login_at, timeZone)}, ${time(p.last_login_at, timeZone)}` : "–"}</td>
                  <td data-label={t("Letzte Ansicht", "Last view")}>{p?.last_view ?? "–"}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={4}>{t("Noch keine Creator.", "No creators yet.")}</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Studio (Creator-Konto): kein Team, aber die Support-Zugänge bleiben einsehbar und beendbar; sie stehen dort unter
 * Anmeldung & Sicherheit (die Seite „Mitglieder und Rechte“ leitet im Studio dorthin um).
 */
export function StudioSupportZugaenge({ orgId, timeZone }: { orgId: string; timeZone: string }) {
  const q = useQuery({ queryKey: ["memberships", orgId, "team"], queryFn: () => loadTeam(api, orgId) });
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  return <SupportZugaenge data={q.data} timeZone={timeZone} />;
}

function SupportZugaenge({ data, timeZone }: { data: TeamData; timeZone: string }) {
  const { busy, run } = useAction();
  const now = Date.now();
  return (
    <section className="panel stack" aria-labelledby="support-title">
      <h3 id="support-title">{t("Support-Zugänge", "Support access")}</h3>
      <p>{ta("Der Plattform-Support liest Agenturdaten nur mit befristetem, begründetem Zugang (höchstens 72 Stunden, nur lesend). Du kannst ihn jederzeit beenden.", "Platform support only reads agency data with time-limited, justified access (at most 72 hours, read-only). You can end it at any time.",
        "Der Plattform-Support liest die Daten deines Kontos nur mit befristetem, begründetem Zugang (höchstens 72 Stunden, nur lesend). Du kannst ihn jederzeit beenden.", "Platform support only reads your account's data with time-limited, justified access (at most 72 hours, read-only). You can end it at any time.")}</p>
      <div className="tablewrap">
        <table>
          <caption className="sr-only">{t("Support-Zugänge der Plattform", "Platform support access")}</caption>
          <thead><tr><th scope="col">{t("Begründung", "Reason")}</th><th scope="col">{t("Zeitraum", "Period")}</th><th scope="col">Status</th><th scope="col">{t("Aktion", "Action")}</th></tr></thead>
          <tbody>
            {data.grants.map((g) => {
              const active = !g.revoked_at && Date.parse(g.starts_at) <= now && Date.parse(g.expires_at) > now;
              return (
                <tr key={g.id}>
                  <td>{g.reason}</td>
                  <td>{date(g.starts_at, timeZone)}, {time(g.starts_at, timeZone)} {t("bis ", "until ")}{date(g.expires_at, timeZone)}, {time(g.expires_at, timeZone)}</td>
                  <td>{active ? <span className="pill warning"><span className="pill-zeichen">◷</span> {t("Aktiv", "Active")}</span> : g.revoked_at ? <span className="pill"><span className="pill-zeichen">○</span> {t("Beendet", "Ended")}</span> : <span className="pill"><span className="pill-zeichen">○</span> {t("Abgelaufen", "Expired")}</span>}</td>
                  <td>{active && <button type="button" className="danger" disabled={!!busy} onClick={() => void run("support", () => endSupportGrant(api, g.id), t("Support-Zugang beendet.", "Support access ended."))}>{t("Zugang beenden", "End access")}</button>}</td>
                </tr>
              );
            })}
            {data.grants.length === 0 && <tr><td colSpan={4}>{t("Bisher kein Support-Zugang.", "No support access so far.")}</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Protokoll({ data, timeZone }: { data: TeamData; timeZone: string }) {
  const members = new Map(data.members.map((m) => [m.id, m.display_name ?? t("Teammitglied", "Team member")]));
  const byUser = new Map(data.members.map((m) => [m.user_id, m.display_name ?? t("Teammitglied", "Team member")]));
  const talents = new Map(data.talents.map((t) => [t.id, talentName(t)]));
  const names = { member: (id: string) => members.get(id) ?? t("Teammitglied", "Team member"), talent: (id: string) => talents.get(id) ?? t("ein Creator", "a creator") };
  return (
    <section className="panel stack" aria-labelledby="protokoll-title">
      <h3 id="protokoll-title">{t("Letzte Änderungen am Team", "Recent team changes")}</h3>
      {data.audit.length === 0 ? <p>{t("Noch keine Änderungen protokolliert.", "No changes logged yet.")}</p> : (
        <div className="tablewrap tk-cardtable">
          <table>
            <caption className="sr-only">{t("Protokoll der Team-Änderungen", "Log of team changes")}</caption>
            <thead><tr><th scope="col">{t("Zeitpunkt", "Time")}</th><th scope="col">{t("Von", "From")}</th><th scope="col">{t("Änderung", "Change")}</th></tr></thead>
            <tbody>
              {data.audit.map((a) => (
                <tr key={a.id}>
                  <td data-label={t("Zeitpunkt", "Time")}>{date(a.at, timeZone)}, {time(a.at, timeZone)}</td>
                  <td data-label={t("Von", "From")}>{a.actor ? byUser.get(a.actor) ?? (a.actor_kind === "platform" ? t("Plattform-Support", "Platform support") : t("Unbekannt", "Unknown")) : "System"}</td>
                  <td data-label={t("Änderung", "Change")}>{auditText(a, names)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
